import { useDeferredValue, useMemo, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { useTranslation } from 'react-i18next'

import { protoDateToCivilDate, requireCivilDate } from '~/models/civilDate'
import { MasterCrudTabShell } from '~/pages/AdminOcctoMaster/shell/MasterCrudTabShell'
import { useMasterCrudTab } from '~/pages/AdminOcctoMaster/shell/useMasterCrudTab'
import { CreateDemandProcurementPlanMasterModal } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/components/CreateDemandProcurementPlanMasterModal'
import { DeleteDemandProcurementPlanMasterDialog } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/components/DeleteDemandProcurementPlanMasterDialog'
import { DemandProcurementPlanMasterTable } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/components/DemandProcurementPlanMasterTable'
import { EditDemandProcurementPlanMasterModal } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/components/EditDemandProcurementPlanMasterModal'
import { useListDemandProcurementMasters } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/hooks/useListDemandProcurementMasters'
import { demandProcurementPlanMasterCsvColumns } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/models/csvColumns'
import type { DemandProcurementMasterRow } from '~/pages/AdminOcctoMaster/tabs/DemandProcurementPlanMaster/types'
import { ExpiredFilterToggle } from '~/pages/AdminOcctoMaster/tabs/_common/components/ExpiredFilterToggle'
import { MasterFilterToolbar } from '~/pages/AdminOcctoMaster/tabs/_common/components/MasterFilterToolbar'

function filterRows(
  rows: DemandProcurementMasterRow[],
  areaCode: AreaCode,
  searchText: string,
): DemandProcurementMasterRow[] {
  const trimmed = searchText.trim().toLowerCase()
  return rows.filter((row) => {
    if (areaCode !== AreaCode.UNSPECIFIED && row.areaCode !== areaCode) {
      return false
    }
    if (trimmed === '') {
      return true
    }
    return (
      row.bgCode.toLowerCase().includes(trimmed) ||
      (row.note?.toLowerCase().includes(trimmed) ?? false)
    )
  })
}

function convertItem(
  item: import('@ebalance/proto/bffuser/v1/occto_master_model_pb').DemandProcurementMasterListItem,
): DemandProcurementMasterRow {
  return {
    id: item.id,
    bgCode: item.bgCode,
    areaCode: item.areaCode,
    note: item.note,
    codeCount: item.codeCount,
    bgCount: item.bgCount,
    updatedBy: item.updatedBy,
    updatedAt: item.updatedAt,
    validFrom: requireCivilDate(
      protoDateToCivilDate(item.validFrom),
      `DemandProcurementMasterListItem.validFrom (id=${item.id})`,
    ),
    validTo: protoDateToCivilDate(item.validTo),
  }
}

/**
 * 需要調達計画マスタ タブ。
 * 一覧 + CRUD モーダルを表示する。
 * 旧 DemandProcurementPartnersTab (= 取引先一覧) を計画マスタ CRUD に作り替えたタブ。
 */
export const DemandProcurementPlanMasterTab = (): React.ReactElement => {
  const { t } = useTranslation()
  const { data, isLoading, isError, error } = useListDemandProcurementMasters()

  const [selectedAreaCode, setSelectedAreaCode] = useState<AreaCode>(
    AreaCode.UNSPECIFIED,
  )
  const [searchText, setSearchText] = useState('')
  const deferredSearchText = useDeferredValue(searchText)

  const rows = useMemo(
    () => (data !== undefined ? data.items.map(convertItem) : []),
    [data],
  )
  const filteredRows = useMemo(
    () => filterRows(rows, selectedAreaCode, deferredSearchText),
    [rows, selectedAreaCode, deferredSearchText],
  )

  const subject = t(
    'page:adminOcctoMaster.common.subject.demandProcurementPlan',
  )
  const tab = useMasterCrudTab<DemandProcurementMasterRow>({
    data,
    isLoading,
    isError,
    error,
    errorKeyPrefix:
      'page:adminOcctoMaster.demandProcurementPlanMaster.serverError',
    filteredRows,
    csv: {
      columns: demandProcurementPlanMasterCsvColumns(),
      fileBase: 'demand-procurement-plan',
    },
    expiry: {
      getPeriod: (row) => ({
        validFrom: row.validFrom,
        validTo: row.validTo,
      }),
      subject,
    },
  })

  const deleteRow = tab.modalState.kind === 'delete' ? tab.modalState.row : null

  return (
    <MasterCrudTabShell
      createModal={
        // Edit モーダルと同じ条件 mount (閉じたら form ごと破棄する)。
        // 常時 mount + isOpen 切替だと、保存成功時の reset() 直後に
        // まだ mount されている codes[] 行入力 (useController) が RHF の
        // 再 register で直前の値を _formValues へ書き戻し、次回 open で
        // 取引先行が残留する (#6072。jsdom では act のバッチングで行が先に
        // unmount するため再現せず、実ブラウザのみで発生)。
        tab.modalState.kind === 'create' ? (
          <CreateDemandProcurementPlanMasterModal
            isOpen
            onClose={tab.closeModal}
          />
        ) : null
      }
      csvExportDisabledReason={tab.csvExportDisabledReason}
      deleteDialog={
        deleteRow !== null ? (
          <DeleteDemandProcurementPlanMasterDialog
            row={deleteRow}
            errorMessage={tab.deleteError?.message}
            isOpen={tab.modalState.kind === 'delete'}
            onClose={tab.closeModal}
            onError={(message) => {
              tab.setDeleteError({ message, retriable: true })
            }}
          />
        ) : null
      }
      editModal={
        tab.modalState.kind === 'edit' ? (
          <EditDemandProcurementPlanMasterModal
            row={tab.modalState.row}
            isOpen
            onClose={tab.closeModal}
          />
        ) : null
      }
      filterToolbar={
        <MasterFilterToolbar
          onSearchTextChange={setSearchText}
          onSelectArea={setSelectedAreaCode}
          searchPlaceholder={t(
            'page:adminOcctoMaster.demandProcurementPlanMaster.toolbar.searchPlaceholder',
          )}
          searchText={searchText}
          selectedAreaCode={selectedAreaCode}
          trailing={
            <ExpiredFilterToggle
              expiredCount={tab.expiredCount}
              includeExpired={tab.includeExpired}
              onChange={tab.setIncludeExpired}
              subject={subject}
            />
          }
        />
      }
      onAddIndividual={tab.openCreate}
      onCsvExport={tab.csvExportEnabled ? tab.handleCsvExport : undefined}
      showInitialLoading={tab.showInitialLoading}
      table={
        <DemandProcurementPlanMasterTable
          emptyLabel={tab.emptyLabel}
          errorLabel={tab.errorLabel}
          isLoading={isLoading}
          onDelete={tab.openDelete}
          onEdit={tab.openEdit}
          rows={tab.visibleRows}
          today={tab.today}
        />
      }
    />
  )
}
